import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { PrivacyToggleProvider } from '@eluve/blocks';
import {
  Layout,
  Main,
  Sidebar,
  SidebarContent,
  SidebarContentSpacer,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarLogoHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuLinkButton,
  SidebarMenuNavButton,
} from '@eluve/components';
import { EluveGptTrigger } from '@eluve/feature-chat-with-patient-data';
import { TemplatesGuard } from '@eluve/frontend-feature-output-templates';
import { useNamedLogger } from '@eluve/logger';
import { usePostHog } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import {
  FeatureFlaggedComponent,
  TenantOrUserFeatureFlaggedComponent,
  UserFeatureFlaggedComponent,
  useTenantName,
} from '@eluve/smart-blocks';
import { useAppointmentRecordingStatusStoreApi } from '@eluve/user-local-files';

import { appConfig } from '../../../../config';
import { TenantUserSettings } from '../../TenantUserSettings';
import { UserDropdownMenu } from '../components/UserDropdownMenu';

import { ImpersonationExpiryMonitor } from './ImpersonationExpiryMonitor';
import { TranscriptionWidget } from './appointments/machines/transcription/components/TranscriptionWidget';
import { useTranscriptionMachineSelector } from './appointments/machines/transcription/hooks/useTranscriptionMachine';

export const TenantLayout = () => {
  const client = useApolloClient();
  const logger = useNamedLogger('TenantLayout');
  const navigate = useNavigate();
  const postHog = usePostHog();
  const tenantId = useAssignedTenantIdFromParams();
  const tenantName = useTenantName(tenantId);
  const userId = useUserIdFromSession();

  const isIdleOrPaused = useTranscriptionMachineSelector(
    (state) => state.matches('Idle') || state.matches({ InSession: 'Paused' }),
  );

  const appointmentRecordingStatusStore =
    useAppointmentRecordingStatusStoreApi();

  useEffect(
    function syncAppointmentRecordingStatus() {
      appointmentRecordingStatusStore.setState({
        isActivelyRecording: !isIdleOrPaused,
      });
    },
    [isIdleOrPaused, appointmentRecordingStatusStore],
  );

  const localStorageKey = `last-tenant-id-for-user-${userId}`;

  const lastTenantId = useMemo(
    () => localStorage.getItem(localStorageKey),
    [localStorageKey],
  );

  useEffect(
    function trackLastAccessedTenantId() {
      if (tenantId !== lastTenantId) {
        localStorage.setItem(localStorageKey, tenantId);
      }
    },
    [tenantId, lastTenantId, client, logger, localStorageKey],
  );

  useEffect(() => {
    postHog.group('tenant', tenantId, {
      name: tenantName,
    });
  }, [postHog, tenantId, tenantName]);

  const handleLogoClick = () => {
    navigate(`/organizations/${tenantId}/home`);
  };

  return (
    <Layout>
      <Sidebar>
        <SidebarHeader>
          <SidebarLogoHeader onClick={handleLogoClick} />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuNavButton
                    icon={{ name: 'Calendar' }}
                    text="Overview"
                    to={`/organizations/${tenantId}/home`}
                  />
                </SidebarMenuItem>
                <FeatureFlaggedComponent flag="APPOINTMENTS_VIEW">
                  <SidebarMenuItem>
                    <SidebarMenuNavButton
                      icon={{ name: 'FileEdit' }}
                      text="Appointments"
                      to={`/organizations/${tenantId}/appointments`}
                    />
                  </SidebarMenuItem>
                </FeatureFlaggedComponent>
                <FeatureFlaggedComponent flag="PATIENTS_VIEW">
                  <SidebarMenuItem>
                    <SidebarMenuNavButton
                      icon={{ name: 'User' }}
                      text="Patients"
                      to={`/organizations/${tenantId}/patients`}
                    />
                  </SidebarMenuItem>
                </FeatureFlaggedComponent>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarContentSpacer />
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu compact>
                <SidebarMenuItem>
                  <UserFeatureFlaggedComponent flag="CHAT_PERSONALIZATION">
                    <SidebarMenuNavButton
                      icon={{ name: 'MessageSquareText' }}
                      text="Chat"
                      to={`/organizations/${tenantId}/chat-settings`}
                      variant="compact"
                    />
                  </UserFeatureFlaggedComponent>
                </SidebarMenuItem>
                <TemplatesGuard>
                  <SidebarMenuItem>
                    <SidebarMenuNavButton
                      icon={{ name: 'LayoutTemplate' }}
                      text="Templates"
                      to={`/organizations/${tenantId}/output-templates`}
                      variant="compact"
                    />
                  </SidebarMenuItem>
                </TemplatesGuard>
                <SidebarMenuItem>
                  <SidebarMenuNavButton
                    icon={{ name: 'Settings' }}
                    text="Settings"
                    to={`/organizations/${tenantId}/settings`}
                    variant="compact"
                  />
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuLinkButton
                    icon={{ name: 'CircleHelp' }}
                    text="Support"
                    href="https://eluveinc.notion.site/Eluve-Support-e7de10ae63384ede8e48a062467bee81"
                    variant="compact"
                  />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <UserDropdownMenu />
        </SidebarFooter>
      </Sidebar>
      <Main>
        <PrivacyToggleProvider>
          <TenantUserSettings>
            <ImpersonationExpiryMonitor />
            <Outlet />
          </TenantUserSettings>
        </PrivacyToggleProvider>
      </Main>
      <TranscriptionWidget />
      <TenantOrUserFeatureFlaggedComponent flag="ELUVE_GPT">
        <EluveGptTrigger
          tenantId={tenantId}
          baseUrl={appConfig.VITE_API_DOMAIN}
        />
      </TenantOrUserFeatureFlaggedComponent>
    </Layout>
  );
};
